import { useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { FormProvider, useForm } from "react-hook-form";

import { useNavigate, useParams } from "react-router-dom";
import { Workflows } from "../../pages/Workflows";
import { usePage } from "../../routes";

import { useWorkflowById } from "../data/useWorkflowById";
import { useWorkflowDelete } from "../data/useWorkflowDelete";
import { useWorkflowUpdate } from "../data/useWorkflowUpdate";

import { Stack } from "@mui/material";
import Alert, { AlertColor } from "@mui/material/Alert";
import Button from "@mui/material/Button";

import EditIcon from "@mui/icons-material/Edit";

import { AccessControlContext } from "../contexts/AccessControlContext";
import { emptyWorkflowV1, emptyWorkflowV2, useWorkflowReducer } from "./workflowReducer";

import DeleteConfirmationButton from "../shared/FormLayout/Buttons/DeleteConfirmationButton";
import { Snackbar } from "../shared/Snackbar";
import { WorkflowV1 } from "../shared/interfaces/WorkflowV1";

import { WorkflowEditUI } from "./WorkflowEditUI";
import { WorkflowUI } from "./WorkflowUI";

import RouteBreadcrumbs from "../shared/RouteBreadcrumbs";
import { WorkflowV2 } from "../shared/interfaces/WorkflowV2";
import config from "../../config";

export function WorkflowViewer() {
  const { t } = useTranslation();
  const workflowPagePath = usePage(Workflows.name)?.path;
  const navigate = useNavigate();
  const formValidation = useForm<WorkflowV1 | WorkflowV2>();
  const initialWorkflow = config.flexiSurveysEnabled
    ? (emptyWorkflowV2 as WorkflowV2)
    : (emptyWorkflowV1 as WorkflowV1);
  const editedWorkflow = useWorkflowReducer(initialWorkflow);
  const { workflowId } = useParams<{ workflowId?: string }>();

  const workflow = useWorkflowById(Number(workflowId), {
    onSuccess: onFetchWorkflowSuccess,
  });
  const workflowUpdate = useWorkflowUpdate({
    onSuccess: onUpdateWorkflowSuccess,
    onError: onUpdateWorkflowError,
  });
  const workflowDelete = useWorkflowDelete({
    onSuccess: onDeleteWorkflowSuccess,
    onError: onDeleteWorkflowError,
  });

  const accessControlContext = useContext(AccessControlContext);
  const userCanEditWorkflow = accessControlContext.canI("edit", "workflow", workflow.data?.customerId);
  const userCanDeleteWorkflow = accessControlContext.canI("delete", "workflow", workflow.data?.customerId);

  const [editingWorkflow, setEditingWorkflow] = useState(false);

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarText, setSnackbarText] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>("success");

  function onFetchWorkflowSuccess(data: WorkflowV1 | WorkflowV2 | undefined) {
    data &&
      !editingWorkflow &&
      editedWorkflow.updateWorkflowDetails({
        type: "setWorkflowFromObject",
        workflow: data,
      });
  }

  function handleEditButtonClick() {
    workflow.data &&
      editedWorkflow.updateWorkflowDetails({
        type: "setWorkflowFromObject",
        workflow: workflow.data,
      });
    setEditingWorkflow(true);
  }

  function saveWorkflowUpdates() {
    if (userCanEditWorkflow && editedWorkflow && editedWorkflow.state.workflow) {
      workflowUpdate.mutate(editedWorkflow.state.workflow);
    }
  }

  function onUpdateWorkflowSuccess() {
    workflow.refetch();
    setEditingWorkflow(false);

    setSnackbarText(t("workflows.workflowChangesSaved", { val: workflowId }));
    setSnackbarSeverity("success");
    setShowSnackbar(true);
    formValidation.reset();
  }

  function onUpdateWorkflowError() {
    setSnackbarText(t("common.dataUpdateError", { val: "$t(workflows.workflow)" }));
    setSnackbarSeverity("error");
    setShowSnackbar(true);
  }

  function deleteWorkflow() {
    userCanDeleteWorkflow && workflow.data && workflowDelete.mutate(workflow.data.id);
  }

  function onDeleteWorkflowSuccess() {
    workflowPagePath && navigate(workflowPagePath, { state: { deletedWorkflowId: workflowId } });
  }

  function onDeleteWorkflowError() {
    setSnackbarText(t("common.dataDeleteError", { val: "$t(workflows.workflow)" }));
    setSnackbarSeverity("error");
    setShowSnackbar(true);
  }

  const onSubmitWorkflowUpdates = formValidation.handleSubmit(() => saveWorkflowUpdates());

  function handleCancelEditButtonClick() {
    workflow.data &&
      editedWorkflow.updateWorkflowDetails({
        type: "setWorkflowFromObject",
        workflow: workflow.data,
      });
    formValidation.reset();
    setEditingWorkflow(false);
  }

  const handleSnackbarClose = useCallback(() => {
    setShowSnackbar(false);
  }, []);

  return (
    <FormProvider {...formValidation}>
      <RouteBreadcrumbs
        customPageTitle={
          workflow.isLoading
            ? t("workflows.workflow")
            : `${t("workflows.workflow")} - ${workflow.data?.description || ""}`
        }
      />

      {!workflow.isLoading && workflow.data && editedWorkflow.state.workflow && (
        <>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            {userCanDeleteWorkflow && !editingWorkflow && (
              <DeleteConfirmationButton
                confirmationDialogHeader={t("common.deleteConfirmationShort", {
                  val: t("workflows.workflow"),
                  transform: "lowercase",
                })}
                confirmationDialogMessage={t("common.deleteConfirmation", {
                  val: t("workflows.workflow"),
                  transform: "lowercase",
                })}
                onConfirmDeleteButtonClick={deleteWorkflow}
              />
            )}

            {userCanEditWorkflow && !editingWorkflow && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<EditIcon />}
                className="ml-3"
                onClick={handleEditButtonClick}
              >
                {t("common.edit")}{" "}
              </Button>
            )}
          </Stack>
          {!workflow.isLoading && workflow.data && editedWorkflow.state.workflow && (
            <>
              {editingWorkflow ? (
                <WorkflowEditUI
                  workflowReducer={editedWorkflow}
                  editMode={editingWorkflow}
                  onSave={onSubmitWorkflowUpdates}
                  saveLoading={workflowUpdate.isLoading}
                  onCancel={handleCancelEditButtonClick}
                />
              ) : (
                <WorkflowUI workflowReducer={editedWorkflow} editMode={editingWorkflow} />
              )}
            </>
          )}

          <Snackbar open={showSnackbar} onClose={handleSnackbarClose}>
            <Alert severity={snackbarSeverity} variant="filled">
              {snackbarText}
            </Alert>
          </Snackbar>
        </>
      )}
    </FormProvider>
  );
}
